/**
 * MobileTabBar — sm(<640px) 전용 하단 탭바
 *
 * 중앙 레지스트리(rail-items.tsx)에서 아이템을 가져옴.
 */

import { Link, useLocation } from "@tanstack/react-router";
import { cn } from "@/shared/lib/utils";
import { MOBILE_TAB_ITEMS } from "@/shared/layouts/rail-items";
import type { RailItem } from "@/shared/layouts/agent-rail";

export interface MobileTabBarProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  className?: string;
}

export function MobileTabBar({
  activeTab,
  onTabChange,
  className,
}: MobileTabBarProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  function isActive(tab: RailItem): boolean {
    if (activeTab) return activeTab === tab.id;
    if (tab.to) {
      return currentPath === tab.to || currentPath.startsWith(tab.to + "/");
    }
    return false;
  }

  return (
    <nav
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 flex sm:hidden",
        "border-t border-stroke-subtle bg-surface-raised",
        "safe-area-inset-bottom",
        className,
      )}
    >
      {MOBILE_TAB_ITEMS.map((tab) => {
        const active = isActive(tab);

        const inner = (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange?.(tab.id)}
            className={cn(
              "flex flex-1 flex-col items-center gap-0.5 px-1 py-2 transition-colors",
              active
                ? "text-text-brand"
                : "text-text-tertiary hover:text-text-primary",
            )}
          >
            {tab.icon}
            <span className="text-[10px] font-medium">{tab.label}</span>
          </button>
        );

        if (tab.to) {
          return (
            <Link
              key={tab.id}
              to={tab.to}
              className="flex flex-1 flex-col items-center"
            >
              {inner}
            </Link>
          );
        }

        return inner;
      })}
    </nav>
  );
}
